import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { usePageVisibility } from 'react-page-visibility';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, useMediaQuery, Grid } from '@mui/material';
import { markAsReadSystemMessages } from '../../api/home';
import { getUnreadData } from '../../redux/slices/dashboard';
import { useDispatch, useSelector } from '../../redux/store';
import Header from './header';
import { Navbar } from './navbar';
import useInterval from '../../reusable-components/use-interval/UseInterval';
import { useTabs } from '../../context/TabContext';
import { refreshLogin } from '../../redux/slices/user';
import RealTimeProviders from '../../context/realtime/RealTimeProviders';

const headerHeightMobile = 56;

const StyledRoot = styled('div')(() => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  return {
    display: 'flex',
    marginLeft: 18,
    marginRight: 18,
    height: isMobile ? `calc(100vh - ${headerHeightMobile + 80}px)` : '',
    maxWidth: '100vw',
    overflowX: 'hidden',
  }
});

const Main = styled('div')(({ theme }) => ({
  flexGrow: 1,
  paddingBottom: theme.spacing(1),
  width: '100%'
}));

const NavStyles = styled('div')(() => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  return {
    paddingTop: isMobile ? 0 : 16,
  };
});

const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return windowWidth;
};

const OutletStyles = styled('div')(() => {
  const screenWidth = useWindowWidth();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  let margin;

  if (screenWidth >= 1920) {
    margin = 100;
  } else if (screenWidth <= 1720) {
    margin = 16;
  } else {
    margin = (screenWidth - 1720) / 2;
  }
  return {
    marginLeft: isMobile ? 0 : `${margin}px`,
    marginRight: isMobile ? 0 : `${margin}px`,
    height: '100%'
  };
});

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const { closeAllTabs } = useTabs();

  const isVisible = usePageVisibility();

  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const { unreadSystemMessages } = useSelector((state) => state.dashboard);
  const [fetchUser, setFetchUser] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentDialogMessage, setCurrentDialogMessage] = useState(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  useEffect(() => {
    if (!user?.id && fetchUser) {
      dispatch(refreshLogin(true));
      setFetchUser(false);
    }
  }, [user, fetchUser]);

  if (user && user.id) {
    Sentry.setUser({
      username: `${user.firstName} ${user.lastName}`,
      id: `${user.firstName} ${user.lastName}`,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }

  const markAsRead = async (ids) => {
    const response = await markAsReadSystemMessages(ids);
    if (response.status === 200) {
      dispatch(getUnreadData());
    }
  };

  useInterval(() => {
    if (user && isVisible && process.env.NODE_ENV === 'production') dispatch(getUnreadData(closeAllTabs, user?.id));
  }, [Math.floor(Math.random() * 30000) + 60000]);

  useEffect(() => {
    if (user && isVisible) dispatch(getUnreadData(closeAllTabs, user?.id));
  }, [dispatch, user, isVisible]);

  useEffect(() => {
    if (unreadSystemMessages?.length > 0) {
      setCurrentDialogMessage(unreadSystemMessages[0]); // Show the first unread message
      setOpenDialog(true);
    } else {
      setOpenDialog(false);
      setCurrentDialogMessage(null);
    }
  }, [unreadSystemMessages]);

  const headerXMargin = 18;
  const headerXPadding = 24;

  return (
    <>
      <Header xMargin={headerXMargin} xPadding={headerXPadding}/>
      {currentDialogMessage && (
        <Dialog
          open={openDialog}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            setOpenDialog(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{currentDialogMessage.title}</DialogTitle>
          <DialogContent dividers>
            {currentDialogMessage.messages &&
              currentDialogMessage.messages
                .filter((msg) => msg)
                .map((msg, idx) => (
                  <div key={idx} style={{ marginBottom: 8 }}>
                    {msg}
                  </div>
                ))}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={async () => {
                await markAsRead([currentDialogMessage.id]);
                setOpenDialog(false);
              }}
              color="primary"
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <StyledRoot>
        <Main>
          <NavStyles>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              spacing={1}
              sx={{ padding: isMobile ? 0 : `0 ${headerXMargin + headerXPadding}px`}}
            >
              <Grid item xs={1} />
              <Grid item xs={11}>
                {!isMobile && <Navbar />}
              </Grid>
            </Grid>
          </NavStyles>
          <OutletStyles>
            <RealTimeProviders>
              <Outlet />
            </RealTimeProviders>
          </OutletStyles>
        </Main>
      </StyledRoot>
    </>
  );
}
